import React, { useMemo, useState, useEffect } from 'react';
import { Button, Checkbox, Dialog, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import { ThemeProvider } from 'styled-components';
import { Datepicker, END_DATE, START_DATE } from '@datepicker-react/styled';
import DmsService from 'Services/DMS/dms-api.service';
import AlertToastr from 'utils/alert';
import { useAuth } from 'Contexts/AuthProvider';
import moment from 'moment';
import { useAppSettings } from '../../../Contexts/AppSettingsProvider';
import useTranslation from 'Hooks/useTranslation';
import { useBoxes } from '../../../Contexts/BoxContext';

const allConst = 'all';
const customConst = 'custom';
const defaultTime = '00:00';
const invalidDatesErrorMessage = '\'Valid From\' must be earlier than \'Valid To\'';

const RadioLabel = ({ label }) => {
    return (
        <div className="jobLabel">
            <div className="labelName">{label}</div>
        </div>
    );
};

const NewUnitPasswordModal = ({ isOpen, isOpenChangeHandler, onRequestClose, companyId, jobs, licensesNames }) => {
    const translation = useTranslation();
    const { appTheme: theme } = useAppSettings();
    const { currentUser } = useAuth();
    const { unitsRequest } = useBoxes();
    const isSuperUser = useMemo(() => (currentUser.isSuperuser()), [currentUser]);
    
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [showDatepickers, setShowDatepickers] = useState(false);
    const [showDataAccessDatepickers, setShowDataAccessDatepickers] = useState(false);
    const [showLicenseIds, setShowLicenseIds] = useState(false);
    const [showJobs, setShowJobs] = useState(false);
    const [licenses, setLicenses] = useState([]);
    const [unitOptions, setUnitOptions] = useState(null);
    const [timeFrom, setTimeFrom] = useState(defaultTime);
    const [timeTo, setTimeTo] = useState(defaultTime);
    const [dataAccessTimeFrom, setDataAccessTimeFrom] = useState(defaultTime);
    const [dataAccessTimeTo, setDataAccessTimeTo] = useState(defaultTime);
    const [user, setUser] = useState({
        selectedLicenses: [],
        selectedJobs: [],
    });
    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null,
        focusedInput: START_DATE,
    });
    const [dataAccessDateRange, setDataAccessDateRange] = useState({
        startDate: null,
        endDate: null,
        focusedInput: START_DATE,
    });

    const { data: units } = unitsRequest || {};

    useEffect(() => {
        if (isOpen) {
            fetchLicenseIds().then();
        }
    }, [isOpen, companyId, isSuperUser]);

    useEffect(() => {
        const fetchUnitOptions = async () => {
            if (selectedUnit?.uId) {
                try {
                    const response = await DmsService.getUnitOptions(selectedUnit.uId);
                    setUnitOptions(response.data);
                } catch (error) {
                    if (error && error.response && error.response.data && error.response.data.Message) {
                        AlertToastr.showErrorAlert(error.response.data.Message);
                    }
                    setUnitOptions(null);
                }
            } else {
                setUnitOptions(null);
            }
        };
        fetchUnitOptions();
    }, [selectedUnit]);

    const fetchLicenseIds = async () => {
        try {
            let response;
            if (isSuperUser)
                response = await DmsService.getLicenseIdsByCompanyId(companyId);
            else
                response = await DmsService.getLicenseIds();
            setLicenses(response.data);
        } catch (error) {
            if (error && error.response && error.response.data && error.response.data.Message)
                AlertToastr.showErrorAlert(error.response.data.Message);
        }
    };

    const handleJobsChange = (item, checked) => {
        let jobList = user.selectedJobs;
        if (checked) {
            jobList.push(item.id);
        } else {
            removeItem(jobList, item.id);
        }
        setUser({ ...user, selectedJobs: jobList });
    };

    const handleLicenseIdsChange = (item, checked) => {
        let licenseList = user.selectedLicenses;
        if (checked) {
            licenseList.push(item.licenseId);
        } else {
            removeItem(licenseList, item.licenseId);
        }
        setUser({ ...user, selectedLicenses: licenseList });
    };

    const handleDatesChange = (data) => {
        if (!data.focusedInput) {
            setDateRange({ ...data, focusedInput: START_DATE });
        } else {
            setDateRange(data);
        }
    };

    const handleDataAccessDatesChange = (data) => {
        if (!data.focusedInput) {
            setDataAccessDateRange({ ...data, focusedInput: START_DATE });
        } else {
            setDataAccessDateRange(data);
        }
    };
    
    useEffect(() => {
        if (dateRange.endDate) {
            setTimeTo('23:59');
        }
    }, [dateRange.endDate]);

    useEffect(() => {
        if (dataAccessDateRange.endDate) {
            setDataAccessTimeTo('23:59');
        }
    }, [dataAccessDateRange.endDate]);

    const removeItem = (list, itemId) => {
        let index = list.findIndex((id) => id === itemId);
        list.splice(index, 1);
    };

    const setDefaultValues = () => {
        setSelectedUnit(null);
        setShowDatepickers(false);
        setShowDataAccessDatepickers(false);
        setShowLicenseIds(false);
        setShowJobs(false);
        setTimeFrom(defaultTime);
        setTimeTo(defaultTime);
        setDataAccessTimeFrom(defaultTime);
        setDataAccessTimeTo(defaultTime);
        setUser({ selectedLicenses: [], selectedJobs: [] });
        setDateRange({ focusedInput: START_DATE, startDate: null, endDate: null });
        setDataAccessDateRange({ focusedInput: START_DATE, startDate: null, endDate: null });
        setUnitOptions(null);
    };

    const formatDate = (date, time) => {
        return new Date(new Date(date).setHours(time.slice(0, 2), time.slice(3, 5))).toISOString();
    };

    const onCreate = () => {
        if (!selectedUnit) {
            AlertToastr.showErrorAlert('Please select a unit');
            return;
        }

        let unitPasswordInfo = createUnitPasswordInfo();
        
        if ((unitPasswordInfo.validity.key === customConst) && new Date(unitPasswordInfo.validity.value.from) >= new Date(unitPasswordInfo.validity.value.to)) {
            AlertToastr.showErrorAlert(invalidDatesErrorMessage);
            return;
        }

        if ((unitPasswordInfo.dataAccessRange.key === customConst) && new Date(unitPasswordInfo.dataAccessRange.value.from) >= new Date(unitPasswordInfo.dataAccessRange.value.to)) {
            AlertToastr.showErrorAlert('\'Data Access From\' must be earlier than \'Data Access To\'');
            return;
        }

        onRequestClose(unitPasswordInfo);
    };

    const createUnitPasswordInfo = () => {
        const uId = selectedUnit?.uId;
        
        return {
            unitUId: uId,
            companyId: companyId,
            validity: {
                key: showDatepickers ? customConst : allConst,
                value: showDatepickers ? {
                    from: formatDate(dateRange.startDate, timeFrom),
                    to: formatDate(dateRange.endDate, timeTo),
                } : {},
            },
            dataAccessRange: {
                key: showDataAccessDatepickers ? customConst : allConst,
                value: showDataAccessDatepickers ? {
                    from: formatDate(dataAccessDateRange.startDate, dataAccessTimeFrom),
                    to: formatDate(dataAccessDateRange.endDate, dataAccessTimeTo),
                } : {},
            },
            allowedLicenses: {
                key: showLicenseIds ? customConst : allConst,
                value: showLicenseIds ? user.selectedLicenses : [],
            },
            allowedJobs: {
                key: showJobs ? customConst : allConst,
                value: showJobs ? user.selectedJobs : [],
            },
        };
    };

    const onClose = (unitPasswordInfo) => {
        isOpenChangeHandler(false);
        onRequestClose(unitPasswordInfo);
        setDefaultValues();
    };

    const onEnter = () => {
        setDefaultValues();
    };

    return (
        <Dialog
            open={isOpen}
            className="respModal scrollContentModal calendarModal autoHeight"
            TransitionProps={{
                onEnter,
            }}
        >
            <form action="">
                <div className="formContentHolder">
                    <div className="modalTitleHolder">
                        <h2>{translation.saveUnitPasswordModalCreateUnitPasswordLabel || 'Create Unit Password'}</h2>
                    </div>
                    <div className="formContent">
                        <div className="formContentBlock">
                            <Autocomplete
                                options={units || []}
                                getOptionLabel={(option) => {
                                    if (!option) return '';
                                    const name = option.nameLocalized || option.name || '';
                                    if (option.uId) {
                                        return `${name} | ${option.uId}`;
                                    }
                                    return name || option.uId || '';
                                }}
                                value={selectedUnit}
                                onChange={(event, newValue) => {
                                    setSelectedUnit(newValue);
                                }}
                                id="settingsNewUnitPasswordModalSelectUnitAutocomplete"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={translation.saveUnitPasswordModalSelectUnitLabel || 'Select Unit'}
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        id="settingsNewUnitPasswordModalSelectUnitInput"
                                    />
                                )}
                                renderOption={(option) => (
                                    <div id="settingsNewUnitPasswordModalChooseUnitOption">
                                        {option.nameLocalized || option.name}
                                        {option.uId && <span>&nbsp;| {option.uId}</span>}
                                    </div>
                                )}
                            />
                        </div>

                        {/* <div className="formContentBlock">
                            <FormControl component="fieldset">
                                <FormLabel component="label"
                                           className={'radioLabel text-bold'}>{translation.saveUserModalValidityTimeLabel}</FormLabel>
                                <RadioGroup row aria-label="position" name="position" defaultValue="lifetime"
                                            onChange={(e) => {
                                                setShowDatepickers(!showDatepickers);
                                            }}>
                                    <FormControlLabel
                                        value="lifetime"
                                        checked={!showDatepickers}
                                        control={<Radio color="default" />}
                                        label={<RadioLabel label={translation.saveUserModalLifetimeLabel} />}
                                    />
                                    <FormControlLabel
                                        value="setTime"
                                        checked={showDatepickers}
                                        control={<Radio color="default" />}
                                        label={<RadioLabel label={translation.saveUserModalSetTimeLabel} />}
                                    />
                                </RadioGroup>
                            </FormControl>
                            {showDatepickers &&
                                <div className="collapseBodyHolder">
                                    <div className="calendarHolder">
                                        <ThemeProvider
                                            theme={{
                                                reactDatepicker: {
                                                    daySize: [36, 40],
                                                    fontFamily: '"Open Sans", sans-serif',
                                                    colors: {
                                                        accessibility: 'transparent',
                                                        selectedDay: theme === 'Light' ? 'rgba(68, 68, 68, 0.05)!important' : 'rgba(255, 255, 255, 0.05)!important',
                                                        selectedDayHover: theme === 'Light' ? 'rgba(68, 68, 68, 0.05)!important' : 'rgba(255, 255, 255, 0.05)!important',
                                                        primaryColor: theme === 'Light' ? 'rgba(68, 68, 68, 0.12)!important' : 'rgba(255, 255, 255, 0.12)!important',
                                                    },
                                                },
                                            }}
                                        >
                                            <Datepicker
                                                onDatesChange={handleDatesChange}
                                                startDate={dateRange.startDate}
                                                endDate={dateRange.endDate}
                                                focusedInput={dateRange.focusedInput}
                                                showResetDates={false}
                                                showSelectedDates={false}
                                                showClose={false}
                                            />
                                        </ThemeProvider>
                                    </div>
                                    <div className="timeHolder">
                                        <div className="inputHolder">
                                            <TextField
                                                id="startTime"
                                                label="Valid from"
                                                type="time"
                                                variant="outlined"
                                                fullWidth
                                                value={timeFrom}
                                                onChange={e => setTimeFrom(e.target.value)}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                inputProps={{
                                                    step: 300,
                                                }}
                                            />
                                        </div>
                                        <div className="inputHolder">
                                            <TextField
                                                id="endtime"
                                                label="Valid to"
                                                type="time"
                                                variant="outlined"
                                                fullWidth
                                                value={timeTo}
                                                onChange={e => setTimeTo(e.target.value)}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                inputProps={{
                                                    step: 300,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>}
                        </div> */}

                        <div className="formContentBlock">
                            <FormControl component="fieldset">
                                <FormLabel component="label"
                                           className={'radioLabel text-bold'}>{translation.saveUnitPasswordModalDataAccessRangeLabel || 'Data Access Range'}</FormLabel>
                                <RadioGroup row aria-label="position" name="dataAccessPosition" defaultValue="lifetime"
                                            onChange={(e) => {
                                                setShowDataAccessDatepickers(!showDataAccessDatepickers);
                                            }}>
                                    <FormControlLabel
                                        value="lifetime"
                                        checked={!showDataAccessDatepickers}
                                        control={<Radio color="default" />}
                                        label={<RadioLabel label={translation.saveUserModalLifetimeLabel} />}
                                    />
                                    <FormControlLabel
                                        value="setTime"
                                        checked={showDataAccessDatepickers}
                                        control={<Radio color="default" />}
                                        label={<RadioLabel label={translation.saveUserModalSetTimeLabel} />}
                                    />
                                </RadioGroup>
                            </FormControl>
                            {showDataAccessDatepickers &&
                                <div className="collapseBodyHolder">
                                    <div className="calendarHolder">
                                        <ThemeProvider
                                            theme={{
                                                reactDatepicker: {
                                                    daySize: [36, 40],
                                                    fontFamily: '"Open Sans", sans-serif',
                                                    colors: {
                                                        accessibility: 'transparent',
                                                        selectedDay: theme === 'Light' ? 'rgba(68, 68, 68, 0.05)!important' : 'rgba(255, 255, 255, 0.05)!important',
                                                        selectedDayHover: theme === 'Light' ? 'rgba(68, 68, 68, 0.05)!important' : 'rgba(255, 255, 255, 0.05)!important',
                                                        primaryColor: theme === 'Light' ? 'rgba(68, 68, 68, 0.12)!important' : 'rgba(255, 255, 255, 0.12)!important',
                                                    },
                                                },
                                            }}
                                        >
                                            <Datepicker
                                                onDatesChange={handleDataAccessDatesChange}
                                                startDate={dataAccessDateRange.startDate}
                                                endDate={dataAccessDateRange.endDate}
                                                focusedInput={dataAccessDateRange.focusedInput}
                                                showResetDates={false}
                                                showSelectedDates={false}
                                                showClose={false}
                                            />
                                        </ThemeProvider>
                                    </div>
                                    <div className="timeHolder">
                                        <div className="inputHolder">
                                            <TextField
                                                id="dataAccessStartTime"
                                                label="Data Access from"
                                                type="time"
                                                variant="outlined"
                                                fullWidth
                                                value={dataAccessTimeFrom}
                                                onChange={e => setDataAccessTimeFrom(e.target.value)}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                inputProps={{
                                                    step: 300,
                                                }}
                                            />
                                        </div>
                                        <div className="inputHolder">
                                            <TextField
                                                id="dataAccessEndtime"
                                                label="Data Access to"
                                                type="time"
                                                variant="outlined"
                                                fullWidth
                                                value={dataAccessTimeTo}
                                                onChange={e => setDataAccessTimeTo(e.target.value)}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                inputProps={{
                                                    step: 300,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>}
                        </div>

                        <div className="formContentBlock">
                            <FormControl component="fieldset">
                                <FormLabel component="label"
                                           className={'radioLabel text-bold'}>{translation.saveUserModalAccessedLicensesLabel}</FormLabel>
                                <RadioGroup row aria-label="position" name="position" defaultValue="top"
                                            onChange={(e) => {
                                                setShowLicenseIds(!showLicenseIds);
                                            }}>
                                    <FormControlLabel
                                        checked={!showLicenseIds}
                                        value="all"
                                        control={<Radio color="default" />}
                                        label={<RadioLabel label={translation.saveUserModalAllLabel} />}
                                    />
                                    <FormControlLabel
                                        checked={showLicenseIds}
                                        value="setIds"
                                        control={<Radio color="default" />}
                                        id="settingsNewUnitPasswordModalSelectLicensesRadio"
                                        label={<RadioLabel label={translation.saveUserModalSelectLicensesLabel} />}
                                    />
                                </RadioGroup>
                            </FormControl>
                            {selectedUnit && showLicenseIds &&
                                <div className="collapseBodyHolder">
                                    <div className="checkboxListHolder mt8">
                                        <div className="labelLike">Licenses</div>
                                        <div className="checkboxListContainer">
                                            {
                                                (() => {
                                                    // Якщо є unitOptions, використовуємо licenses з них
                                                    const licensesToShow = unitOptions?.licenses 
                                                        ? unitOptions.licenses
                                                            .map(licenseId => licensesNames?.find(l => l.licenseId === licenseId))
                                                            .filter(Boolean)
                                                        : licenses;
                                                    
                                                    return licensesToShow.map(item => (
                                                        <div key={item.licenseId}>
                                                            <label>
                                                                <Checkbox color="default" name={item.name}
                                                                          checked={user.selectedLicenses.includes(item.licenseId)}
                                                                          id={`settingsNewUnitPasswordModalSelectLicenseCheckbox`}
                                                                          onChange={(e) => {
                                                                              handleLicenseIdsChange(item, e.target.checked);
                                                                          }} />
                                                                {item.name}
                                                            </label>
                                                        </div>
                                                    ));
                                                })()
                                            }
                                        </div>
                                    </div>
                                </div>}
                        </div>

                        <div className="formContentBlock">
                            <FormControl component="fieldset">
                                <FormLabel component="label"
                                           className={'radioLabel text-bold'}>{translation.saveUserModalAccessedJobsLabel}</FormLabel>
                                <RadioGroup row aria-label="position" name="position" defaultValue="top"
                                            onChange={(e) => {
                                                setShowJobs(!showJobs);
                                            }}>
                                    <FormControlLabel
                                        checked={!showJobs}
                                        value="all"
                                        control={<Radio color="default" />}
                                        label={<RadioLabel label={'All'} />}
                                    />
                                    <FormControlLabel
                                        checked={showJobs}
                                        value="setJob"
                                        control={<Radio color="default" />}
                                        id="settingsNewUnitPasswordModalSelectJobsRadio"
                                        label={<RadioLabel label={translation.saveUserModalSelectJobsLabel} />}
                                    />
                                </RadioGroup>
                            </FormControl>
                            {selectedUnit && showJobs &&
                                <div className="collapseBodyHolder">
                                    <div className="checkboxListHolder mt8">
                                        <div className="labelLike">{translation.saveUserModalJobsLabel}</div>
                                        <div className="checkboxListContainer">
                                            {(() => {
                                                // Якщо є unitOptions, використовуємо jobs з них
                                                const jobsToShow = unitOptions?.jobs || jobs;
                                                
                                                return jobsToShow && jobsToShow.map(item => (
                                                    <div key={item.id}>
                                                        <label>
                                                            <Checkbox color="default" name={item.identifier}
                                                                      checked={user.selectedJobs.includes(item.id)}
                                                                      id={`settingsNewUnitPasswordModalSelectJobCheckbox`}
                                                                      onChange={(e) => handleJobsChange(item, e.target.checked)} />
                                                            {item.name}
                                                        </label>
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    </div>
                                </div>}
                        </div>
                    </div>
                    <div className="btnHolder end fixedWidthBtn">
                        <Button onClick={(e) => onClose(null)}
                                variant="outlined">
                            {translation.cancelButtonLabel}
                        </Button>
                        <Button onClick={onCreate}
                                variant="contained"
                                id="settingsNewUnitPasswordModalCreateBtn"
                                color="primary">
                            {translation.createButtonLabel}
                        </Button>
                    </div>
                </div>
            </form>
            <style>{`
        /* Autocomplete specific styles to match select dropdown */
        .MuiAutocomplete-root .MuiOutlinedInput-root {
          min-height: 35px !important;
          height: 35px !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-input {
          padding: 8.5px 14px !important;
          line-height: 1.1876em !important;
          height: auto !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-root {
          padding: 0 !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-input::placeholder {
          line-height: 1.1876em !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-marginDense {
          min-height: 35px !important;
          height: 35px !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-root {
          display: flex !important;
          align-items: center !important;
          padding: 0 !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-input {
          margin: 0 !important;
          padding: 8.5px 14px !important;
        }
        
        .MuiAutocomplete-listbox {
          padding: 0 !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option {
          padding: 4px 8px !important;
          min-height: auto !important;
          font-size: 13px !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option:hover {
          background-color: rgba(0, 0, 0, 0.04) !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"] {
          background-color: rgba(0, 0, 0, 0.08) !important;
        }
        
        /* Checkbox styling in autocomplete */
        .MuiAutocomplete-listbox .MuiCheckbox-root {
          padding: 4px !important;
          margin-right: 6px !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root .MuiSvgIcon-root {
          font-size: 18px !important;
        }
        
        /* Ensure consistent spacing */
        .MuiAutocomplete-listbox .MuiAutocomplete-option > div {
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
        }
        
        /* Checkbox colors in autocomplete */
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked svg {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked path {
          fill: #006ae7 !important;
        }
        
        /* Keep unchecked checkboxes in their original color */
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) .MuiSvgIcon-root {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) svg {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) path {
          fill: inherit !important;
        }
      `}</style>
        </Dialog>
    );
};

export default NewUnitPasswordModal;

